/**
 * useStatusPolling — Workflow status polling hook.
 *
 * Extracted from WorkflowMachineContext.jsx during Story 4.6 wrap-up.
 * Polls the backend's getWorkflowStatus endpoint every 5 seconds while
 * a thread is active, dispatches UPDATE_SESSION_DATA on each tick, and
 * stops automatically when the workflow reaches a terminal/paused state
 * or all 9 agents have completed.
 *
 * Dispatch goes through the residue's bridge dispatch — UPDATE_SESSION_DATA
 * is routed to WorkflowResultsContext.
 */
import { useEffect } from 'react';
import logger from '../../../utils/logger';
import { getWorkflowStatus } from '../../../api/workflow';

export function useStatusPolling({
  backendServiceRef,
  stateMachineRef,
  statusPollingIntervalRef,
  dispatch,
  threadId,
}) {
  useEffect(() => {
    logger.debug('📊 Polling useEffect running', {
      hasThreadId: !!backendServiceRef.current?.threadId,
      threadId: backendServiceRef.current?.threadId,
      hasExistingInterval: !!statusPollingIntervalRef.current
    });

    const startStatusPolling = () => {
      // Only poll if we have an active session
      if (!backendServiceRef.current?.threadId) {
        logger.debug('⏭️ No threadId, skipping polling start');
        return;
      }

      // Prevent duplicate polling
      if (statusPollingIntervalRef.current) {
        logger.debug('⚠️ Status polling already active, skipping start', {
          intervalId: statusPollingIntervalRef.current
        });
        return;
      }

      logger.debug('🔄 Starting workflow status polling for reactive updates', {
        threadId: backendServiceRef.current?.threadId,
        timestamp: new Date().toISOString()
      });

      statusPollingIntervalRef.current = setInterval(async () => {
        logger.debug('🔍 Poll iteration starting', {
          timestamp: new Date().toISOString(),
          intervalId: statusPollingIntervalRef.current,
          stateMachine: stateMachineRef.current?.currentState
        });

        try {
          // Skip global polling if an agent is actively processing
          // This prevents duplicate polling when step components are polling
          const currentState = stateMachineRef.current?.currentState;
          const isAgentActivelyPolling =
            currentState === 'processing' ||
            currentState === 'PROCESSING';

          if (isAgentActivelyPolling) {
            logger.debug('⏸️ Skipping global poll - agent actively polling', {
              currentState
            });
            return;
          }

          // Story 6.1: stateless mirror — passes threadId from the ref.
          const workflowStatus = await getWorkflowStatus(backendServiceRef.current.threadId);

          // Update session data with latest workflow status
          dispatch({
            type: 'UPDATE_SESSION_DATA',
            payload: {
              session_status: workflowStatus.status,
              current_agent: workflowStatus.current_agent,
              completed_agents: workflowStatus.agents_completed || []
            }
          });

          logger.debug('🔄 Workflow status updated:', {
            status: workflowStatus.status,
            currentAgent: workflowStatus.current_agent,
            completedAgents: workflowStatus.agents_completed
          });

          // Stop polling if workflow is in terminal state or paused waiting for user
          const shouldStopPolling =
            workflowStatus.status === 'completed' ||
            workflowStatus.status === 'failed' ||
            workflowStatus.status === 'cancelled' ||
            workflowStatus.status === 'error' ||
            workflowStatus.status === 'paused' || // Stop when paused (waiting for user)
            (workflowStatus.agents_completed && workflowStatus.agents_completed.length >= 9); // All agents done

          logger.debug('🔍 Stop condition evaluation', {
            status: workflowStatus.status,
            shouldStopPolling,
            completedAgents: workflowStatus.agents_completed?.length || 0,
            conditions: {
              isCompleted: workflowStatus.status === 'completed',
              isFailed: workflowStatus.status === 'failed',
              isCancelled: workflowStatus.status === 'cancelled',
              isError: workflowStatus.status === 'error',
              isPaused: workflowStatus.status === 'paused',
              allAgentsDone: workflowStatus.agents_completed && workflowStatus.agents_completed.length >= 9
            }
          });

          if (shouldStopPolling) {
            logger.debug('🛑 Calling stopStatusPolling - workflow not actively processing:', {
              status: workflowStatus.status,
              reason: workflowStatus.status === 'paused' ? 'Waiting for user action' : 'Terminal state',
              intervalId: statusPollingIntervalRef.current
            });
            stopStatusPolling();
          } else {
            logger.debug('✅ Continuing polling', {
              status: workflowStatus.status,
              nextPollIn: '5 seconds'
            });
          }
        } catch (error) {
          logger.warn('Failed to poll workflow status:', error);
        }
      }, 5000); // Poll every 5 seconds
    };

    const stopStatusPolling = () => {
      logger.debug('⏹️ stopStatusPolling called', {
        hasInterval: !!statusPollingIntervalRef.current,
        intervalId: statusPollingIntervalRef.current
      });

      if (statusPollingIntervalRef.current) {
        const intervalId = statusPollingIntervalRef.current;
        logger.debug('⏹️ Clearing interval and nullifying ref', {
          intervalId
        });
        clearInterval(statusPollingIntervalRef.current);
        statusPollingIntervalRef.current = null;
        logger.debug('✅ Polling stopped successfully', {
          clearedIntervalId: intervalId,
          currentRef: statusPollingIntervalRef.current
        });
      } else {
        logger.debug('⚠️ No active interval to stop');
      }
    };

    // Start polling if we have an active session
    if (backendServiceRef.current?.threadId) {
      logger.debug('🚀 Starting polling from useEffect', {
        threadId: backendServiceRef.current?.threadId
      });
      startStatusPolling();
    } else {
      logger.debug('⏭️ No threadId in useEffect, not starting polling');
    }

    // Cleanup on unmount or when threadId changes
    return () => {
      logger.debug('🧹 useEffect cleanup triggered', {
        hasInterval: !!statusPollingIntervalRef.current,
        reason: 'unmount or threadId change'
      });
      stopStatusPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]);
}
